import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import type { PermissionLevel } from '../shared/api';
import { Banner, Button, Card, Field, Input } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';

/**
 * First-run office interview (Run 8). Not a settings tour: we ask how the shop
 * runs, they type emails ("that's me" / skip), and the answers become ordinary
 * invites on THIS office at the mapped seat. Shown once — finishing or
 * skipping stamps officeSetupCompletedAt; /team stays the month-two editor.
 */

interface RowState {
  email: string;
  me: boolean;
  skipped: boolean;
}

type RowKey = 'noo' | 'affidavit' | 'ap' | 'intake';
/** Brokerage interview (Run 9): same mechanics, sale-side jobs. */
type BrokerageRowKey = 'broker' | 'desk' | 'compliance';

const PROMPTS: Array<{ key: RowKey; prompt: string; hint: string; many?: boolean }> = [
  { key: 'noo', prompt: 'Who sends your Notices to Owner?', hint: 'Joins as Manager — can send certified mail from the office wallet.' },
  { key: 'affidavit', prompt: 'Who signs affidavits / final sign-off?', hint: 'Joins as Admin (Manager if it is your NTO sender). Never a field seat.' },
  { key: 'ap', prompt: 'Who pays ABC / supplier invoices?', hint: 'Joins as Manager — marking paid is spend. Separate several emails with commas.', many: true },
  { key: 'intake', prompt: 'Who intakes jobs / customers?', hint: 'Joins as Field — logs work, cannot send or sign. Separate several emails with commas.', many: true },
];

/** Same splitter sales already uses — one invite per address, not one blob. */
function emailsIn(raw: string): string[] {
  return raw.split(/[,\s]+/).map((s) => s.trim().toLowerCase()).filter((s) => s.includes('@'));
}

const BROKERAGE_PROMPTS: Array<{ key: BrokerageRowKey; prompt: string; hint: string }> = [
  { key: 'broker', prompt: 'Who is the managing broker / broker of record?', hint: 'Joins as Admin — runs the roster. That’s you unless someone else holds the license.' },
  { key: 'desk', prompt: 'Who runs the transaction desk / may invite agents onto files?', hint: 'Joins as Manager — may add agents to this brokerage.' },
  { key: 'compliance', prompt: 'Read-only compliance / owner watching?', hint: 'Joins as Read-only — sees the files, changes nothing. Optional.' },
];

const RANK: Record<PermissionLevel, number> = { read_only: 0, field: 1, manager: 2, admin: 3 };

const blankRow = (): RowState => ({ email: '', me: false, skipped: false });

/**
 * Fold the answers into invites: one invite per email at the higher of its
 * seats ('admin' > 'manager' > 'field'), never the owner, never two companies.
 * The affidavit signer is admin unless they are the same person as the NOO
 * sender, where one manager seat covers both jobs.
 */
export function planInvites(
  rows: Record<RowKey, RowState>,
  sales: string,
  ownerEmail: string,
): Array<{ email: string; permissionLevel: PermissionLevel }> {
  const owner = ownerEmail.trim().toLowerCase();
  const want = new Map<string, PermissionLevel>();
  const norm = (r: RowState) => (r.skipped || r.me ? null : r.email.trim().toLowerCase() || null);
  const consider = (email: string | null, seat: PermissionLevel) => {
    if (!email || !email.includes('@') || email === owner) return;
    const prev = want.get(email);
    if (!prev || RANK[seat] > RANK[prev]) want.set(email, seat);
  };

  const noo = norm(rows.noo);
  consider(noo, 'manager');
  const affidavit = norm(rows.affidavit);
  consider(affidavit, affidavit !== null && affidavit === noo ? 'manager' : 'admin');
  if (!rows.ap.skipped) for (const e of emailsIn(rows.ap.email)) consider(e, 'manager');
  if (!rows.intake.skipped) for (const e of emailsIn(rows.intake.email)) consider(e, 'field');
  for (const e of emailsIn(sales)) consider(e, 'field');

  return [...want.entries()].map(([email, permissionLevel]) => ({ email, permissionLevel }));
}

/**
 * Brokerage mapping (Run 9): managing broker → admin, transaction desk →
 * manager, listing agents → field (they oversee nobody), compliance →
 * read-only. Same folding rules — one invite per email at the higher seat,
 * never the owner, never a new company.
 */
export function planBrokerageInvites(
  rows: Record<BrokerageRowKey, RowState>,
  agents: string,
  ownerEmail: string,
): Array<{ email: string; permissionLevel: PermissionLevel }> {
  const owner = ownerEmail.trim().toLowerCase();
  const want = new Map<string, PermissionLevel>();
  const norm = (r: RowState) => (r.skipped || r.me ? null : r.email.trim().toLowerCase() || null);
  const consider = (email: string | null, seat: PermissionLevel) => {
    if (!email || !email.includes('@') || email === owner) return;
    const prev = want.get(email);
    if (!prev || RANK[seat] > RANK[prev]) want.set(email, seat);
  };

  consider(norm(rows.broker), 'admin');
  consider(norm(rows.desk), 'manager');
  for (const raw of agents.split(/[,\s]+/)) consider(raw.trim().toLowerCase() || null, 'field');
  consider(norm(rows.compliance), 'read_only');

  return [...want.entries()].map(([email, permissionLevel]) => ({ email, permissionLevel }));
}

export default function OfficeSetup() {
  const { contractor } = useSession();
  const navigate = useNavigate();
  // Brokerages get the sale-side prompts; the managing broker defaults to the
  // account holder ("that's me"), since they are the one signing up.
  const brokerage = contractor?.companyKind === 'brokerage';
  const [rows, setRows] = useState<Record<string, RowState>>(
    (): Record<string, RowState> =>
      brokerage
        ? { broker: { ...blankRow(), me: true }, desk: blankRow(), compliance: blankRow() }
        : { noo: blankRow(), affidavit: blankRow(), ap: blankRow(), intake: blankRow() },
  );
  const [sales, setSales] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Show once: an office that already finished (or skipped) goes to the roster.
  if (contractor?.officeSetupCompletedAt) return <Navigate to="/team" replace />;

  const setRow = (key: string, patch: Partial<RowState>) => setRows((r) => ({ ...r, [key]: { ...r[key], ...patch } }));

  const finish = async (invite: boolean) => {
    setBusy(true);
    setError(null);
    try {
      if (invite) {
        const planned = brokerage
          ? planBrokerageInvites(rows as Record<BrokerageRowKey, RowState>, sales, contractor?.email ?? '')
          : planInvites(rows as Record<RowKey, RowState>, sales, contractor?.email ?? '');
        for (const p of planned) {
          try {
            await api.post('/api/team/invite', { email: p.email, displayName: null, permissionLevel: p.permissionLevel });
          } catch (err) {
            // Already on the roster is fine — the interview never forks a seat.
            if (!(err instanceof ApiError && err.status === 409)) throw err;
          }
        }
      }
      // Stamps officeSetupCompletedAt so the interview shows once.
      await api.post('/api/team/setup', {});
      navigate('/team', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not finish office setup');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-xl font-bold text-ink">How does {contractor?.companyName} run?</h1>
        <p className="mt-1 text-sm text-muted">
          Type the email of whoever does each job and LienGuardian gives them the right office seat.
        </p>
        <p className="text-sm font-medium text-ink">
          We invite them to <strong>{brokerage ? 'this brokerage' : 'this office'}</strong>. They do not get their own
          company.
        </p>
      </div>

      {error && <Banner tone="urgent">{error}</Banner>}

      <Card>
        <div className="space-y-5">
          {(brokerage ? BROKERAGE_PROMPTS : PROMPTS).map((p) => {
            const { key, prompt, hint } = p;
            const many = 'many' in p && p.many;
            const row = rows[key];
            return (
              <div key={key} className="space-y-1">
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Field label={prompt} hint={hint}>
                      <Input
                        type={many ? 'text' : 'email'}
                        placeholder={many ? 'name@company.com, other@company.com' : 'name@company.com'}
                        value={row.email}
                        disabled={row.skipped || (row.me && !many)}
                        onChange={(e) => setRow(key, { email: e.target.value })}
                      />
                    </Field>
                  </div>
                  <Button
                    type="button"
                    variant={row.me ? 'primary' : 'secondary'}
                    aria-pressed={row.me}
                    onClick={() => setRow(key, { me: !row.me, skipped: false })}
                    className="whitespace-nowrap px-3 py-1.5 text-xs"
                  >
                    That’s me
                  </Button>
                  <Button
                    type="button"
                    variant={row.skipped ? 'primary' : 'ghost'}
                    aria-pressed={row.skipped}
                    onClick={() => setRow(key, { skipped: !row.skipped, me: false })}
                    className="px-3 py-1.5 text-xs"
                  >
                    Skip
                  </Button>
                </div>
                {row.me && (
                  <p className="text-xs text-muted">
                    {many
                      ? 'That’s you — you already hold the admin seat. Add anyone else who does this job.'
                      : 'That’s you — you already hold the admin seat, no invite needed.'}
                  </p>
                )}
              </div>
            );
          })}

          <Field
            label={brokerage ? 'Listing agents on this office?' : 'Do you have sales people on this office?'}
            hint={
              brokerage
                ? 'Joins as Field — a listing agent cannot oversee other agents. Separate emails with commas.'
                : 'Joins as Field. Separate several emails with commas — not a send seat.'
            }
          >
            <Input
              placeholder="ana@company.com, luis@company.com"
              value={sales}
              onChange={(e) => setSales(e.target.value)}
            />
          </Field>
        </div>
      </Card>

      <div className="flex items-center justify-between">
        <Button type="button" variant="ghost" disabled={busy} onClick={() => void finish(false)}>
          Skip for now
        </Button>
        <Button type="button" disabled={busy} onClick={() => void finish(true)}>
          {busy ? 'Setting up…' : brokerage ? 'Set up my brokerage' : 'Set up my office'}
        </Button>
      </div>
      <p className="text-xs text-muted">You can change any seat later under Team.</p>
    </div>
  );
}
